import { useMemo, useEffect } from 'react';
import { Source, Layer, useMap } from 'react-map-gl';
import type { CircleLayer, SymbolLayer } from 'react-map-gl';
import { MapLocation } from '@/types/location';
import { getIconName, ensureDynamicIcon } from '@/utils/mapboxIcons';
import { LocationOverride } from '@/hooks/useLocationOverrides';

interface MapboxMarkerLayerProps {
  filteredLocations: MapLocation[];
  selectedLocations: Set<number>;
  overrides?: Map<number, LocationOverride>;
}

const getMarkerColor = (location: MapLocation): string => {
  const { mediaOwner, type, segment } = location;

  if (mediaOwner === 'Attention') return '#BC1A6C';
  if (mediaOwner === 'Waki') return '#10B981';
  if (mediaOwner === 'Rotana Signs') return '#0b7b41';
  if (mediaOwner === 'On Spot') return '#E91E63';

  if (mediaOwner === 'Alan') {
    if (type.toLowerCase() === 'gym') return '#8B5CF6';
    if (type.toLowerCase() === 'game center') return '#F59E0B';
    if (segment?.toLowerCase() === 'shisha' || segment?.toLowerCase() === 'lounge') return '#7E69AB';
    if (segment?.toLowerCase() === 'cafe') return '#8B4513';
    return '#8B5CF6';
  }

  switch (type.toLowerCase()) {
    case 'cafe': return '#8B4513';
    case 'restaurant': return '#FF4500';
    case 'lounge': return '#2478C8';
    case 'gym': return '#8B5CF6';
    case 'game center': return '#F59E0B';
    case 'school': return '#10B981';
    case 'building screens':
    case 'outdoor billboard':
    case 'digital megacom':
    case 'digital super mega':
    case 'curved led screen':
    case 'led screen':
      return '#BC1A6C';
    case 'barbershop':
    case 'salon':
      return '#E91E63';
    default: return '#6366f1';
  }
};

export const MapboxMarkerLayer = ({ filteredLocations, selectedLocations, overrides }: MapboxMarkerLayerProps) => {
  const { current: mapInstance } = useMap();

  // Register dynamic custom icons when overrides change
  useEffect(() => {
    const map = mapInstance?.getMap();
    if (!map || !overrides) return;

    const registerCustomIcons = async () => {
      for (const [locId, override] of overrides) {
        const ed = override.extra_details as any;
        if (ed?.custom_icon && ed?.custom_icon_color) {
          const iconKey = `custom-${ed.custom_icon}-${ed.custom_icon_color.replace('#', '')}`;
          await ensureDynamicIcon(map, iconKey, ed.custom_icon, ed.custom_icon_color);
        }
      }
    };
    registerCustomIcons();
  }, [overrides, mapInstance]);

  const geojsonData = useMemo(() => {
    const features = filteredLocations.map((location) => {
      const override = overrides?.get(location.id);
      const ed = override?.extra_details as any;
      const customIcon = ed?.custom_icon;
      const customColor = ed?.custom_icon_color;

      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [location.coordinates[1], location.coordinates[0]],
        },
        properties: {
          locationId: location.id,
          name: location.name,
          type: location.type,
          mediaOwner: location.mediaOwner,
          color: customColor || getMarkerColor(location),
          iconName: getIconName(location, customIcon, customColor),
          isSelected: selectedLocations.has(location.id) ? 1 : 0,
        },
      };
    });

    return {
      type: 'FeatureCollection' as const,
      features,
    };
  }, [filteredLocations, selectedLocations, overrides]);

  const selectedRingLayer: CircleLayer = {
    id: 'location-markers-selected-ring',
    type: 'circle',
    source: 'locations',
    filter: ['==', ['get', 'isSelected'], 1],
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        4, 28,
        8, 36,
        12, 46,
        14, 54,
        16, 64,
        18, 78,
        20, 96,
      ],
      'circle-color': 'transparent',
      'circle-stroke-width': 3,
      'circle-stroke-color': '#3b82f6',
      'circle-stroke-opacity': 0.9,
    },
  };

  const iconLayer: SymbolLayer = {
    id: 'location-markers-icons',
    type: 'symbol',
    source: 'locations',
    layout: {
      'icon-image': ['get', 'iconName'],
      'icon-size': [
        'interpolate',
        ['linear'],
        ['zoom'],
        4, 0.9,
        8, 1.2,
        12, 1.6,
        14, 1.9,
        16, 2.3,
        18, 2.9,
        20, 3.5,
      ],
      'icon-allow-overlap': true,
      'icon-anchor': 'center',
    },
  };

  const labelLayer: SymbolLayer = {
    id: 'location-markers-labels',
    type: 'symbol',
    source: 'locations',
    minzoom: 11,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
      'text-size': 12,
      'text-letter-spacing': 0.03,
      'text-offset': [0, 1.8],
      'text-anchor': 'top',
      'text-max-width': 10,
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#1e293b',
      'text-halo-color': '#ffffff',
      'text-halo-width': 2,
      'text-halo-blur': 0.5,
    },
  };

  return (
    <Source id="locations" type="geojson" data={geojsonData}>
      <Layer {...selectedRingLayer} />
      <Layer {...iconLayer} />
      <Layer {...labelLayer} />
    </Source>
  );
};
